import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Output,
  inject,
} from '@angular/core';
import { ViewChild } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SearchInputComponent } from '../../inputs/search-input/search-input.component';
import {
  StatusFilterComponent,
  StatusFilterOption,
} from '../../inputs/status-filter/status-filter.component';
import { DataTableComponent } from '../../ui/data-table/data-table.component';
import { PaginationComponent } from '../../inputs/pagination/pagination.component';
import { PromptCardComponent } from '../../toasts-and-cards/prompt-card/prompt-card.component';
import { EditUploadedContactComponent } from '../../organization/contacts/csv/edit-uploaded-contact/edit-uploaded-contact.component';
import { ContactDetailComponent } from '../../organization/contacts/contact-detail/contact-detail.component';
import { StatusComponent } from '../../ui/status/status.component';
import { EmptyStateComponent } from '../../ui/empty-state/empty-state.component';
import { CsvServiceService } from '../../../services/csv/csv-service.service';
import { IConstraintViolation, IValidatedContactBatchLine } from '../../../types/contactModule';
import { cn } from '../../../../utils/utils';

type ICleanupFilter = 'All' | 'Valid' | 'Invalid';

/** Contact field names as people read them, for "Phone: Number has no country code". */
export const IMPORT_FIELD_LABELS: Record<string, string> = {
  primaryContactNumber: 'Phone',
  emailAddress: 'Email',
  birthDate: 'Birth date',
  country: 'Country',
  firstName: 'First name',
  lastName: 'Last name',
  gender: 'Gender',
  city: 'City',
};

/** The real problems on a row (drops the "Successful" placeholder). */
export function rowIssues(line: IValidatedContactBatchLine | null | undefined): IConstraintViolation[] {
  return (line?.constraintViolations ?? []).filter((v) => !!v?.message && v.message !== 'Successful');
}

export function issueText(issue: IConstraintViolation): string {
  const field = IMPORT_FIELD_LABELS[issue.propertyName] ?? '';
  return field ? `${field}: ${issue.message}` : issue.message;
}

@Component({
  selector: 'csv-cleanup-table',
  standalone: true,
  imports: [
    MatTooltipModule,
    SearchInputComponent,
    StatusFilterComponent,
    DataTableComponent,
    PaginationComponent,
    PromptCardComponent,
    EditUploadedContactComponent,
    ContactDetailComponent,
    StatusComponent,
    EmptyStateComponent,
  ],
  templateUrl: './csv-cleanup-table.component.html',
  styleUrls: ['./csv-import-table.css', './csv-cleanup-table.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsvCleanupTableComponent {
  @ViewChild(SearchInputComponent) searchInput?: SearchInputComponent;

  csvService = inject(CsvServiceService);
  private cdr = inject(ChangeDetectorRef);
  merge = cn;
  readonly issueText = issueText;

  /** Tells the step a row was edited or removed, so its summary refreshes. */
  @Output() rowsChanged = new EventEmitter<void>();

  filterTitle: ICleanupFilter = 'All';
  searchTerm: string = '';

  readonly filterOptions: StatusFilterOption[] = [
    { value: 'All', title: 'All rows', icon: 'list', tone: 'neutral' },
    { value: 'Valid', title: 'Ready', icon: 'check_circle', tone: 'success' },
    { value: 'Invalid', title: 'Needs fixing', icon: 'error', tone: 'danger' },
  ];

  // Modal state
  openEditContactModal = false;
  showContactDetail = false;
  showDeletePrompt = false;
  selectedContact: IValidatedContactBatchLine | null = null;
  selectedContactIndex: string | number | null = null;

  // Cache filtered contacts + counts to avoid recomputing on every CD cycle.
  private _cachedFiltered: IValidatedContactBatchLine[] = [];
  private _lastFilter: ICleanupFilter = 'All';
  private _lastSearch: string = '';
  private _lastVersion: number = -1;
  private _lastRef: IValidatedContactBatchLine[] | null = null;
  private _cachedTotal = 0;
  private _cachedInvalid = 0;

  // Pagination — only the current page's rows are rendered into the DOM, so
  // imports with thousands of rows stay responsive.
  pageSize = 50;
  currentPage = 0;

  private invalidateCache() {
    this._lastVersion = -1;
  }

  updateFilterOption(option: ICleanupFilter) {
    this.filterTitle = option || 'All';
    this.currentPage = 0;
    this.invalidateCache();
    this.cdr.markForCheck();
  }

  updateSearchTerm(term: string) {
    this.searchTerm = term;
    this.currentPage = 0;
    this.invalidateCache();
    this.cdr.markForCheck();
  }

  clearSearchAndFilter() {
    this.searchInput?.writeValue('');
    this.filterTitle = 'All';
    this.searchTerm = '';
    this.currentPage = 0;
    this.invalidateCache();
    this.cdr.markForCheck();
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredContacts.length / this.pageSize));
  }

  get pagedContacts(): IValidatedContactBatchLine[] {
    const list = this.filteredContacts;
    const lastPage = Math.max(0, Math.ceil(list.length / this.pageSize) - 1);
    const page = Math.min(Math.max(0, this.currentPage), lastPage);
    const start = page * this.pageSize;
    return list.slice(start, start + this.pageSize);
  }

  goToPage(page: number) {
    this.currentPage = page;
    this.cdr.markForCheck();
  }

  nextPage() {
    this.currentPage += 1;
    this.cdr.markForCheck();
  }

  prevPage() {
    if (this.currentPage > 0) this.currentPage -= 1;
    this.cdr.markForCheck();
  }

  changePageSize(size: number) {
    this.pageSize = size;
    this.currentPage = 0;
    this.cdr.markForCheck();
  }

  private clampCurrentPage() {
    const lastPage = Math.max(0, Math.ceil(this.filteredContacts.length / this.pageSize) - 1);
    if (this.currentPage > lastPage) this.currentPage = lastPage;
  }

  private ensureCache() {
    const contacts = this.csvService.getValidatedContacts;
    const version = this.csvService.version;
    if (
      this._lastVersion === version &&
      this._lastRef === contacts &&
      this._lastFilter === this.filterTitle &&
      this._lastSearch === this.searchTerm
    ) {
      return;
    }
    let invalid = 0;
    for (const c of contacts) {
      if (c.batchLineValidationStatus === 'PROCESSING_ERROR') invalid++;
    }
    this._cachedTotal = contacts.length;
    this._cachedInvalid = invalid;

    let filtered: IValidatedContactBatchLine[];
    switch (this.filterTitle) {
      case 'Valid':
        filtered = contacts.filter((c) => c.batchLineValidationStatus === 'PROCESSED_OK');
        break;
      case 'Invalid':
        filtered = contacts.filter((c) => c.batchLineValidationStatus === 'PROCESSING_ERROR');
        break;
      default:
        filtered = contacts;
    }

    const term = this.searchTerm.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter((c) => {
        const p = c.personEntity;
        return (
          (p.firstName || '').toLowerCase().includes(term) ||
          (p.lastName || '').toLowerCase().includes(term) ||
          (p.emailAddress || '').toLowerCase().includes(term) ||
          (p.primaryContactNumber || '').toString().toLowerCase().includes(term)
        );
      });
    }

    this._cachedFiltered = filtered;
    this._lastVersion = version;
    this._lastRef = contacts;
    this._lastFilter = this.filterTitle;
    this._lastSearch = this.searchTerm;
  }

  get totalCount(): number {
    this.ensureCache();
    return this._cachedTotal;
  }

  get invalidCount(): number {
    this.ensureCache();
    return this._cachedInvalid;
  }

  get validCount(): number {
    this.ensureCache();
    return this._cachedTotal - this._cachedInvalid;
  }

  get filteredContacts(): IValidatedContactBatchLine[] {
    this.ensureCache();
    return this._cachedFiltered;
  }

  /** Called by the step after a bulk fix changed rows in place. */
  refresh() {
    this.invalidateCache();
    this.clampCurrentPage();
    this.cdr.markForCheck();
  }

  rowKey(line: IValidatedContactBatchLine, index: number): string {
    return String(line.personEntity?.id ?? `i-${index}`);
  }

  rowNumber(line: IValidatedContactBatchLine): number | null {
    return this.csvService.rowNumber(line);
  }

  name(line: IValidatedContactBatchLine): string {
    const p = line.personEntity;
    return `${p?.firstName ?? ''} ${p?.lastName ?? ''}`.trim() || 'No name';
  }

  issues(line: IValidatedContactBatchLine): IConstraintViolation[] {
    return rowIssues(line);
  }

  openEditContact(contact: IValidatedContactBatchLine) {
    this.selectedContact = contact;
    this.selectedContactIndex = contact.personEntity.id!;
    this.openEditContactModal = true;
    this.cdr.markForCheck();
  }

  closeEditContact() {
    this.openEditContactModal = false;
    // An edit can flip validation status without changing the array length.
    this.invalidateCache();
    this.rowsChanged.emit();
    this.cdr.markForCheck();
  }

  openContactDetail(contact: IValidatedContactBatchLine) {
    this.selectedContact = contact;
    this.showContactDetail = true;
    this.cdr.markForCheck();
  }

  /** Enter on the row opens it; Enter on a row button stays with the button. */
  openFromKeyboard(event: Event, contact: IValidatedContactBatchLine) {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.openContactDetail(contact);
  }

  closeContactDetail() {
    this.showContactDetail = false;
    this.cdr.markForCheck();
  }

  openDeletePrompt(contact: IValidatedContactBatchLine) {
    this.selectedContact = contact;
    this.selectedContactIndex = contact.personEntity.id!;
    this.showDeletePrompt = true;
    this.cdr.markForCheck();
  }

  closeDeletePrompt() {
    this.showDeletePrompt = false;
    this.cdr.markForCheck();
  }

  deleteContact() {
    if (this.selectedContactIndex !== null) {
      this.csvService.deleteValidatedContact(this.selectedContactIndex);
      this.invalidateCache();
      this.clampCurrentPage();
      this.closeDeletePrompt();
      this.rowsChanged.emit();
    }
  }
}
